import executeGraphQlRequest from './executeGraphQlRequest';
import {
  submitButtonFragment,
  formConfirmationFragment,
  captchaFieldFragment,
  checkboxFieldFragment,
  dateFieldFragment,
  emailFieldFragment,
  hiddenFieldFragment,
  htmlFieldFragment,
  numberFieldFragment,
  phoneFieldFragment,
  radioFieldFragment,
  selectFieldFragment,
  multiSelectFieldFragment,
  textareaFieldFragment,
  textFieldFragment,
  sectionFieldFragment,
  fileuploadFieldFragment,
  nameFieldFragment,
  consentFieldFragment,
} from '../fragments/GravityForms';

const submitMutation = `
  mutation submitForm($id: ID!, $fieldValues: [FormFieldValuesInput]!) {
    submitGfForm(input: { id: $id, fieldValues: $fieldValues }) {
      errors { id message }
    }
  }
`;

const gravityFormQuery = `
  query GetGravityForms($ids: [ID!]) {
    gfForms(where: {formIds: $ids}) {
      nodes {
        databaseId description descriptionPlacement labelPlacement subLabelPlacement title
        submitButton { ${submitButtonFragment} }
        confirmations { ${formConfirmationFragment} }
        hasHoneypot
        formFields {
          nodes {
            displayOnly id: databaseId inputType layoutGridColumnSpan layoutSpacerGridColumnSpan
            pageNumber type visibility
            ${captchaFieldFragment} ${checkboxFieldFragment} ${dateFieldFragment} ${emailFieldFragment}
            ${hiddenFieldFragment} ${htmlFieldFragment} ${numberFieldFragment} ${phoneFieldFragment}
            ${radioFieldFragment} ${selectFieldFragment} ${multiSelectFieldFragment}
            ${textareaFieldFragment} ${textFieldFragment} ${sectionFieldFragment} ${fileuploadFieldFragment} ${nameFieldFragment}
          }
        }
        consentFields: formFields(where: {fieldTypes: [CONSENT]}) {
          nodes { displayOnly id: databaseId inputType layoutGridColumnSpan layoutSpacerGridColumnSpan pageNumber type visibility ${consentFieldFragment} }
        }
      }
    }
  }
`;

type GravityFormFieldNode = {
  id: number;
  displayOnly: boolean;
  inputType: string;
  layoutGridColumnSpan: number;
  layoutSpacerGridColumnSpan: number;
  pageNumber: number;
  type: string;
  visibility: string;
  [key: string]: unknown;
};

type GravityFormNodes = {
  formFields: { nodes: GravityFormFieldNode[] };
  consentFields?: { nodes: GravityFormFieldNode[] };
};

type GravityFormsGraphQLResponse = {
  gfForms: { nodes: GravityFormNodes[] };
};

export type GravityFormResponse = {
  nodes: GravityFormNodes[];
  submit: { query: string; cmsUrl: string };
};

export default async function fetchGravityForms(
  cmsUrl: string,
  ids: number[],
): Promise<GravityFormResponse | null> {
  if (!ids.length) return null;
  const data = (await executeGraphQlRequest({
    cmsUrl,
    query: gravityFormQuery,
    variables: { ids },
  })) as GravityFormsGraphQLResponse;

  const nodes = [
    ...(data.gfForms.nodes[0]?.formFields.nodes || []),
    ...(data.gfForms.nodes[0]?.consentFields?.nodes || []),
  ].filter(
    (field, index, self) => index === self.findIndex((f) => f.id === field.id),
  );

  return {
    nodes: [
      {
        ...data.gfForms.nodes[0],
        formFields: { nodes },
      },
    ],
    submit: { query: submitMutation, cmsUrl },
  };
}
